import React, { FC, useCallback, useState, useMemo, useEffect, Fragment } from 'react';
import { createUseStyles } from 'react-jss';
import colors from '@packages/core/styles/colors';
import { Typography, Icon, Button } from '@packages/ui/shared';
import { analyticsService, feedbackService, httpClient, qmrsService } from '@web/services/singletons';
import { useAbortController } from '@packages/core/http';
import { FeedbackFormAnswer, FeedbackFormTypeId, FeedbackForm } from '@packages/models/api';
import { FeedbackFormElement } from '@web/feedback/components';
import { fetchQmr, useQmrDispatch, useQmrState } from '@packages/contexts/qmrs';
import { ANALYTICS_EVENTS } from '@packages/core/analytics';
import { useTranslation } from 'react-i18next';
import constants from '@web/config/constants';
import { qmrActions } from '@packages/contexts/qmrs';
import { getLogger } from '@packages/core/logging';
import { FormElementTypeId } from '@packages/models/api';

const logger = getLogger('qmr');

interface QmrFeedbackDrawerProps {
    isCollapsed: boolean;
}

const useStyles = createUseStyles({
    panel: ({ isCollapsed }: QmrFeedbackDrawerProps) => ({
        backgroundColor: colors.white,
        bottom: 0,
        boxShadow: '-8px 0 24px rgba(0, 0, 0, 0.10), -2px 0 8px rgba(0, 0, 0, 0.16)',
        display: 'flex',
        flexDirection: 'column',
        maxWidth: 400,
        position: 'absolute',
        right: 0,
        top: constants.headerHeight,
        transform: isCollapsed ? 'translateX(calc(100% - 15px))' : 'translateX(0)',
        transition: 'transform 0.2s ease',
        width: '100%',
        zIndex: 10,
    }),
    panelHeader: {
        alignItems: 'center',
        borderBottom: `1px solid ${colors.grayThree}`,
        display: 'flex',
        height: 56,
        justifyContent: 'space-between',
        padding: '4px 24px 0px 24px',
        backgroundColor: colors.grayTwo,
    },
    panelScrollContainer: ({ isCollapsed }: QmrFeedbackDrawerProps) => ({
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        overflowX: isCollapsed ? 'hidden' : 'auto',
        overflowY: 'auto',
        padding: 24,
    }),
    submitButton: {
        alignSelf: 'flex-start',
        marginTop: 'auto',
    },
    collapseButton: {
        height: 24,
        width: 24,
        '&:hover': {
            cursor: 'pointer',
        },
    },
    divider: {
        backgroundColor: colors.grayThree,
        height: 1,
        marginBottom: 24,
    },
    expandButton: {
        alignItems: 'center',
        backgroundColor: colors.blueFour,
        borderRadius: '50%',
        boxShadow: '0px 2px 4px 0px rgba(0, 0, 0, 0.16)',
        display: 'flex',
        height: 24,
        justifyContent: 'center',
        width: 24,
    },
    expandButtonOuter: {
        position: 'fixed',
        right: 0,
        top: constants.headerHeight + 15,
        zIndex: 15,
        '&:hover': {
            cursor: 'pointer',
        },
    },
});

const Divider: FC<{ className?: string }> = ({ className }) => <hr className={className} />;
const renderDivider = (index: number, length: number, className?: string) => {
    return index < length - 1 ? <Divider className={className} /> : null;
};

export function QmrFeedbackDrawer() {
    const qmrDispatch = useQmrDispatch();
    const { qmr, isQmrFeedbackDrawerCollapsed } = useQmrState();
    const classes = useStyles({ isCollapsed: isQmrFeedbackDrawerCollapsed });
    const { t } = useTranslation();
    const { abortSignalRef } = useAbortController();

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [feedbackFormId, setFeedbackFormId] = useState('');
    const [qmrFeedbackForm, setQmrFeedbackForm] = useState<FeedbackForm>();
    const [sessionChoices, setSessionChoices] = useState<Record<string, FeedbackFormAnswer>>({});

    const hasAnsweredChoices = useMemo(() => {
        if (!qmrFeedbackForm?.formElements) {
            return false;
        }
        const requiredFields = qmrFeedbackForm?.formElements.filter(
            (element) =>
                element.formElementTypeId !== FormElementTypeId.TextHeader &&
                element.formElementTypeId !== FormElementTypeId.TextParagraph
        );

        const answeredFieldCount = Object.values(sessionChoices).length;
        const totalRequiredFieldCount = requiredFields.length;

        return answeredFieldCount === totalRequiredFieldCount;
    }, [sessionChoices]);

    const setQmrFeedbackDrawerCollapsed = useCallback(
        (isCollapsed: boolean) => {
            qmrDispatch(qmrActions.setQmrFeedbackDrawerCollapsed(isCollapsed));
        },
        [qmrDispatch]
    );

    const handleCollapse = () => {
        setQmrFeedbackDrawerCollapsed(true);
    };

    const handleExpand = () => {
        setQmrFeedbackDrawerCollapsed(false);
    };
    useEffect(() => {
        fetchQmrFeedbackForm();
    }, [abortSignalRef]);

    const fetchQmrFeedbackForm = useCallback(() => {
        feedbackService
            .fetchQmrFeedbackForm({ signal: abortSignalRef.current })
            .then((response) => {
                if (!response.success && response.aborted) {
                    return;
                } else if (!response.success) {
                    throw response.data;
                }

                const { feedbackForm } = response.data;
                setQmrFeedbackForm(feedbackForm);
                setFeedbackFormId(feedbackForm.feedbackFormId);
                setSessionChoices(() => {
                    const ans: { [key: string]: FeedbackFormAnswer } = {};
                    feedbackForm.formElements.forEach((ele) => {
                        if (ele.selectedOptions?.length) {
                            ans[ele.formElementId] = {
                                elementId: ele.formElementId,
                                options: ele.selectedOptions,
                            };
                        }
                    });
                    return ans;
                });
            })
            .catch((e) => {
                logger.error('Failed to fetch the QMR feedback form', { error: e });
            });
    }, [abortSignalRef]);

    const handleAnswer = useCallback((newAnswer: FeedbackFormAnswer) => {
        setSessionChoices((prev) => ({
            ...prev,
            [newAnswer.elementId]: newAnswer,
        }));
    }, []);

    async function handleQmrFeedbackSubmission() {
        if (!qmr || !feedbackFormId) {
            return;
        }

        try {
            const choices = Object.values(sessionChoices);
            httpClient.refreshToken();
            setIsSubmitting(true);

            const response = await feedbackService.submitQmrFeedback({
                qmrId: qmr.qmrId,
                feedbackFormId,
                answers: { choices },
                signal: abortSignalRef.current,
            });

            if (!response.success && response.aborted) {
                return;
            } else if (!response.success) {
                throw response.data;
            }

            analyticsService.logEvent(ANALYTICS_EVENTS.USER_GIVES_FEEDBACK, {
                feedbackType: FeedbackFormTypeId.Qmr,
                tsId: qmr.stsIdentifier,
            });

            setIsSubmitting(false);

            try {
                const fetchController = new AbortController();

                fetchQmr({
                    qmrId: qmr.qmrId,
                    qmrsService,
                    qmrDispatch,
                    signal: fetchController.signal,
                    ignoreCache: true,
                });
            } catch (error) {
                logger.warn(`Failed to fetch the updated QMR [${qmr.qmrId}]`, { error, qmrId: qmr.qmrId });
            }

            qmrDispatch(qmrActions.setQmrFeedbackDrawerVisible(false));
        } catch (error) {
            setIsSubmitting(false);
            alert(error.message || 'An error occurred while submitting feedback.');
        }
    }

    return (
        <>
            {isQmrFeedbackDrawerCollapsed && (
                <div className={`${classes.expandButtonOuter}`} onClick={handleExpand} data-testid="expand-button">
                    <div className={`${classes.expandButton}`}>
                        <Icon name="chevron-left" color="black" size={16} />
                    </div>
                </div>
            )}
            <div className={classes.panel}>
                <div className={classes.panelHeader} data-testid="header">
                    <Typography variant="default" testID="title">
                        {t('qmr:feedback.title', 'QMR Feedback')}
                    </Typography>
                    <div className={classes.collapseButton} onClick={handleCollapse} data-testid="collapse-button">
                        <Icon name="chevron-right" color="black" size={20} />
                    </div>
                </div>
                <div className={classes.panelScrollContainer} data-testid="drawer-container">
                    {qmrFeedbackForm?.formElements.map((element, index) => (
                        <Fragment key={element.formElementId}>
                            <FeedbackFormElement
                                key={element.formElementId}
                                item={{
                                    ...element,
                                    selectedOptions: sessionChoices[element.formElementId]?.options,
                                    required: true,
                                }}
                                onAnswerChange={handleAnswer}
                                value={sessionChoices[element.formElementId]?.options[0]?.answerText}
                            />
                            {renderDivider(index, qmrFeedbackForm.formElements.length, classes.divider)}
                        </Fragment>
                    ))}
                    <div className={`${classes.submitButton}`}>
                        <Button
                            isLoading={isSubmitting}
                            variant="primary"
                            disabled={!hasAnsweredChoices}
                            onPress={handleQmrFeedbackSubmission}
                            testID="submit-button"
                        >
                            {t('qmr:feedback.actions.submit', 'Submit & finish')}
                        </Button>
                    </div>
                </div>
            </div>
        </>
    );
}
